import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Column } from 'react-table'
import Table from '../../components/Table'
import { Button, Checkbox, Container, Divider, FormControl, FormControlLabel, Grid, InputLabel, ListItemText, makeStyles, MenuItem, Select, Typography } from '@material-ui/core'
import DateFnsUtils from '@date-io/date-fns'
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers'
import * as StatsAPI from '../../api/statistics/statistics'
import * as StatsModels from '../../api/statistics/models'
import moment from "moment"
import "moment/locale/da"
import daLocale from "date-fns/locale/da"
import NoDataDialog from '../../components/dialogs/ErrorDialog'
import APIRequest from '../../components/helpers/APIRequest'
import LoadingSymbols from '../../utils/loadingSymbol'
import * as DLData from "../../pages/digitalLicens/digitalLicensDataTemplates"
import { getLicenseStatus } from '../../utils/digitallicensContext'
import * as mobilbase from '../../api/mobilbase';
import axios from 'axios'
import { trDate, trAmount } from '../../utils/tr'
import DigitalLicensTable from '../../components/Table/DigitalLicensTable'

moment.locale("da")

const useStyles = makeStyles((theme) => ({
    paper: {
        [theme.breakpoints.up('sm')]: {
            padding: '0',
        },
        marginTop: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    buts: {
        width: '100%',
        height: '100%',
        padding: '0',
        marginTop: '0.5rem'
    },
    autoFrom: {
        width: '100%',
        height: '100%',
        marginTop: '0.5rem'
    },
    autoForm:{
        maxHeight: "60px",
        overflow: "auto"
    },
    grid: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(3),
    },
    textHelp: {
        marginTop: '1rem'
    },
    margTop: {
        marginTop: theme.spacing(2),
    }
}))

const DigitalLicensesStatistics: FC = () => {
    const classes = useStyles()

    // Fetch data
    const [data, setData] = useState<StatsModels.DigitalLicensesStatsResponse>(new StatsModels.DigitalLicensesStatsResponse())
    const [summaryData, setSummaryData] = useState<StatsModels.DigitalLicensesStatsData[]>([])
    const [isPending, setIsPending] = useState(false)
    const [run, setRun] = useState(false)
    const [showError, setShowError] = useState(false)
    const [fetchData, setFetchData] = useState(false)
    const [downloadData, setDownloadData] = useState(false)

    // Filter properties
    const [requestType, setRequestType] = useState<number>(0)
    const [licenseType, setLicenseType] = useState<number>(-1)
    const [areas, setAreas] = useState<StatsModels.AreaEntry[]>(DLData.ParkArea)
    const [selectedAreas, setSelectedAreas] = useState<number[]>([])
    const [statusArr] = useState<StatsModels.StatusEntry[]>(DLData.LicensStatusDanish)
    const [statusArrInvoicing] = useState<StatsModels.StatusEntry[]>(DLData.LicensStatusInvoicingDanish)
    const [chosenStatusType, setChosenStatusType] = useState<any>(DLData.LicensStatusDanish)
    const [selectedStatus, setSelectedStatus] = useState<number[]>([])
    const [fromDate, setFromDate] = useState<Date | null>(new Date())
    const [toDate, setToDate] = useState<Date | null>(new Date())

    // Other properties
    const [pendingLoadingPicture, setPendingLoadingPicture] = useState(false)
    const [areaLabelText, setAreaLabelText] = useState("Områder - alle valgt")
    const [selectAreaBool, setSelectAreaBool] = useState(false)
    const [statusLabelText, setStatusLabelText] = useState("Status - alle valgt")
    const [selectStatusBool, setSelectStatusBool] = useState(false)

    // Necessary for avoiding strikethrough on the comboboxes
    const labelRequestRef = useRef(null)
    const labelAreaRef = useRef(null)
    const labelStatusRef = useRef(null)
    const labelTypeRef = useRef(null)
    
    var keyList = [""]

    useMemo(() => {
        let invoiceArr = requestType === 0 ? statusArr : statusArrInvoicing
        setChosenStatusType(invoiceArr)
        setSelectedStatus([])
    }, [requestType])

    useEffect(() => {
        if (selectedAreas.length === 0){
            setAreaLabelText("Områder - alle valgt")
        }
        else{
            setAreaLabelText("Områder")
        }
    },[selectedAreas])

    useEffect(() => {
        if (selectedStatus.length === 0){
            setStatusLabelText("Status - alle valgt")
        }
        else{
            setStatusLabelText("Status")
        }
    },[selectedStatus])

    useEffect(() => {
        function handleKeyDown(event: any){
            const keyname = event.key
            keyList.push(keyname)
    
            if (event.shiftKey && keyList.includes("S") && keyList.includes("E")) {
                setDownloadData(true)
            }
            if (event.shiftKey && keyList.includes("S") && keyList.includes("H") ) {
                setFetchData(true)
            }
        }
        document.addEventListener("keydown", handleKeyDown)

        return function cleanUP() {
            document.removeEventListener("keydown", handleKeyDown)
        }
    })
    
    useEffect(() => {
        function handleKeyUp(event: any){
            keyList = [""]
            setDownloadData(false)
            setFetchData(false)
        }
        document.addEventListener("keyup", handleKeyUp)

        return function cleanUP() {
            document.removeEventListener("keyup", handleKeyUp)
        }
    })

    useEffect( () => {
        if (downloadData){
            downloadCSV()
        }
    }, [downloadData])

    useEffect( () => {
        if (fetchData){
            return setRun(true)
        }
    }, [fetchData])

    useEffect(() => {  // show data fetching loading picture
        if (!isPending){
            setPendingLoadingPicture(false)
        }
        else{
            setPendingLoadingPicture(true)
        }
    }, [isPending])

    useEffect(() => {
        if (data && data.result.length > 0) {
            setSummaryData([data.result[data.result.length-1]])
        } else {
            setSummaryData([])
        }
    }, [data])

    function compareNumericString(rowA: any, rowB: any, id: any, desc: any) {
        let a = parseFloat(rowA.values[id].replace(".", ""))
        let b = parseFloat(rowB.values[id].replace(".", ""))

        if (rowA.values[0] === "Total") {  // find total row, and put it last or first
            a = desc ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
        }
        if (rowB.values[0] === "Total") {
            b = desc ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
        }
        if (a > b) return 1; 
        if (a < b) return -1;
        return 0;
    }

    enum ConventionDays {
        Monday = 1 << 0, // 1
        Tuesday = 1 << 1, // 2
        Wednesday = 1 << 2, // 4
        Thursday = 1 << 3, // 8
        Friday = 1 << 4, // 16
        Saturday = 1 << 5, // 32
        Sunday = 1 << 6, // 64
        Other = 1 << 7, // 128
      }
    
      function isDaySet(day: ConventionDays, val: number) {
          return Boolean(day & val);
      }

      const renderPeriodDayType = (type: number) => {
        switch (type) {
            case 0: return "days"
            case 1: return "weeks"
            case 2: return "months"
            case 3: return "years"
            case 4: return "years"
        }
    }

    const renderAcceptedDate = (licensRow: any, accDate: any) => {
        let rowId = licensRow.id
        if (typeof rowId === "string"){ // The total row is a string, whereas all the other rows are numbers 
            return ""
        }
        if (accDate !== null){
            if (accDate !== undefined && accDate !== "1900-01-01T00:00:00"){
                return trDate(new Date (accDate))
            }
            else{
                return ""
            }
            }
        else{
            return ""
        }
    }

    function renderStartDate(licensRow: any, originalDate: any, modifiedDate: any){
        let rowId = licensRow.id
        if (typeof rowId === "string"){ // The total row is a string, whereas all the other rows are numbers 
            return ""
        }
        if (modifiedDate !== null){
                if (modifiedDate !== undefined){
                    return trDate(new Date (modifiedDate)) + " kl.00:00"
                }
                else{
                    return trDate(new Date (originalDate)) + " kl.00:00"
                }
            }
        else{
            return trDate(new Date (originalDate)) + " kl.00:00"
        }
    }

    const renderEndDate = (licensRow: any, endDate: Date) => {
        
        let rowId = licensRow.id
        if (typeof rowId === "string"){ // The total row is a string, whereas all the other rows are numbers 
            return ""
        }
        if (endDate !== null){
                return trDate(new Date (endDate)) + " kl.24:00"
            }
        else{
            return ""
        }
    }

    const renderPaidDate = (licensRow: any, paidDate: any) => {
        let rowId = licensRow.id
        if (typeof rowId === "string"){ // The total row is a string, whereas all the other rows are numbers 
            return ""
        }

        if (paidDate !== null){
            return trDate(new Date(paidDate))
        }
        else{
            return ""
        }
    }

    function compareDateString(rowA: any, rowB: any) {
        let a = rowA.original.emptyTime 
        let b = rowB.original.emptyTime

        if (a > b) return 1; 
        if (a < b) return -1;
        return 0;
    }

    function showLicensName(licens: number) {
        let switchType
        if (licens !== null) {
            switchType = licens
        }
        else{
            switchType = 5
        }
        switch (switchType) {
            case -1: return "" // Used for total row
            case 0: return "Parkeringstilladelser"  
            case 1: return "Beboer- og erhvervslicens" 
            case 2: return "Håndværkerlicens"  
            case 3: return "Dispensationer"  
            case 4: return "Landsstævnet 2022" 
            
        }
        return "?"
    }

    const columnsSummary: Column<StatsModels.DigitalLicensesStatsData>[] = React.useMemo(() => [
        {Header: "Antal ansøgninger", id: "0", accessor: "id"},
        {Header: "Total pris", id: "1", accessor: a => trAmount(a.price100/100)},
        {Header: "Antal biler", id: "2", accessor: "plate"}
    ], [])

    const exportColumnsParkant: Column<StatsModels.DigitalLicensesStatsData>[] = React.useMemo(() => [ // This column is used when exporting to excel. in the document
        {Header: "Ref.nr.", id: "0", accessor: "id"}, 
        {Header: "Licenstype", id: "1", accessor: "licenseType" },
        {Header: "Område", id: "3", accessor: "area"},
        {Header: "Pris", id: "4"}, // The price needs to be formatted in the backend, this is why the accessor is missing
        {Header: "Start", id: "5", accessor: "startDate", sortType: compareDateString},
        {Header: "Slut", id: "6", accessor: "endDate", sortType: compareDateString},
        {Header: "Godk.", id: "7", accessor: "acceptedDate", sortType: compareDateString},
        {Header: "Betalt", id: "8", accessor: "paymentDate", sortType: compareDateString},
        {Header: "Status", id: "9"}, // Same reason here and with "Reg. nr."
        {Header: "Reg. nr.", id: "13"} 
    ], [])

    const columnsParkant: Column<StatsModels.DigitalLicensesStatsData>[] = React.useMemo(() => [
        {Header: "Ref.nr.", id: "0", accessor: "id"}, 
        {Header: "Licenstype", id: "1", accessor: a => showLicensName(a.licenseType) },
        //{Header: "Periode", id: "2", accessor: "period"},
        {Header: "Område", id: "3", accessor: "area"},
        {Header: "Pris", id: "4", accessor: a => trAmount(a.price100/100)}, 
        {Header: "Start", id: "5", accessor: a => renderStartDate(a, a.startDate, a.modifiedStartDate), sortType: compareDateString},
        {Header: "Slut", id: "6", accessor: a => renderEndDate(a, a.endDate), sortType: compareDateString},
        {Header: "Godk.", id: "7", accessor: a => renderAcceptedDate(a, a.acceptedDate), sortType: compareDateString},
        {Header: "Betalt", id: "8", accessor: a => renderPaidDate(a, a.paymentDate), sortType: compareDateString},
        {Header: "Status", id: "9", accessor: a => getLicenseStatus(a, a.licenseType, a.modifiedLicenseType, a.status, a.startDate, a.count, a.period)},
        //{Header: "Navn ", id: "10", accessor: "name"},
        //{Header: "Firmanavn", id: "11", accessor: "companyName"},
        //{Header: "CVR.", id: "12", accessor: "cvr"},
        {Header: "Reg. nr.", id: "13", accessor: a => a.plate.split(";").join(", ") + " "} //  Only visual effect with split & join
    ], [])

    const RequestData = () => {
        const fixDate = (date: Date | null, startOfDay: boolean) => {
            // We have to send the date with a UTC timezone, or the server will get the value wrong
            var d1 = date ? date : new Date()
            var d = new Date(Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate(), 0, 0, 0, 0))
            return d
        }

        const param = (): StatsModels.DigitalLicensesStatsParams => {
            var areasList = areas.filter((x) => selectedAreas.indexOf(x.id) > -1);
            var statusList = chosenStatusType.filter((x: any) => selectedStatus.indexOf(x.id) > -1);
            var requestTypeParam = requestType === 0 ? "requests" : "invoicing"
            return {requestType: requestTypeParam, start: fixDate(fromDate, true), end: fixDate(toDate, false), licenseType: licenseType, areas: areasList, status: statusList}
        }

        return (<APIRequest<StatsModels.DigitalLicensesStatsResponse>
            run={run} setRun={setRun}
            pending={isPending} setIsPending={setIsPending}
            req={() => StatsAPI.getDigitalLicensesStats(param())}
            setData={setData}
            setShowError={setShowError}
        />)
    }

    const handleRequestTypeChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        setRequestType(event.target.value as number)
        // Clear the fetched values
        setData(new StatsModels.DigitalLicensesStatsResponse())
    }

    const handleLicenseTypeChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        updateLicenseType(event.target.value as number)
    }

    const updateLicenseType = (type: number) => {
        if (type !== licenseType && type >= -1 && type <= 4) {
            setLicenseType(type)
            setSelectAreaBool(false)

            // Update the possible areas to set
            switch (type) {
                case 0: setAreas(DLData.ParkArea); break // Parkeringstilladelser
                case 1: setAreas(DLData.LivingArea); break // Beboer- og erhvervslicens
                case 2: setAreas(DLData.CraftmanArea); break // Håndværkerlicens
                case 3: setAreas(DLData.DispensionArea); break // Dispensationer
                case 4: setAreas(DLData.ConventionArea); break // Landsstævnetilladelse
                default: setAreas([])
            }
            setSelectedAreas([])

            // Clear the fetched values
            setData(new StatsModels.DigitalLicensesStatsResponse())
        }
    }

    const handleAreasChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const type = event.target.value as number[]
        updateAreasSelection(type)
    }

    const handleStatusChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
        const type = event.target.value as number[]
        updateStatusSelection(type)
    }

    const updateAreasSelection = (newSelection: number[] | null) => {
        if (newSelection) {
            setSelectedAreas(newSelection)
        } else {
            setSelectedAreas([])
        }
    }

    const updateStatusSelection = (newSelection: number[] | null) => {
        if (newSelection) {
            setSelectedStatus(newSelection)
        } else {
            setSelectedStatus([])
        }
    }

    const selectAllAreas = () => {
        let newArr: React.SetStateAction<number[]> = []
        if (!selectAreaBool){
            for (let index = 0; index < areas.length; index++) {
                const element = areas[index];
                newArr.push(index)
            }
            setSelectedAreas(newArr)
            setSelectAreaBool(true)
        }
        else{
            setSelectedAreas(newArr)
            setSelectAreaBool(false)
        }
    }

    const selectAllStatus = () => {
        let newArr: React.SetStateAction<number[]> = []

        if (!selectStatusBool){
            for (let index = 0; index < chosenStatusType.length; index++) {
                const element = areas[index];
                newArr.push(index)
            }
            setSelectedStatus(newArr)
            setSelectStatusBool(true)
        }
        else{
            setSelectedStatus(newArr)
            setSelectStatusBool(false)
        }
    }

    const downloadPriceList = async () => {
        setPendingLoadingPicture(true)
        let fileNameURL = `${process.env.REACT_APP_MOBILBASE_API}/api/v1/statistics/pricelist/` // Endpoint from "LicenseRequestController.cs"
        axios({
            url: fileNameURL,
            method: 'GET',
            responseType: 'blob',
        }).then((response) => {
            const href = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('download', 'Pris liste.pdf');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(fileNameURL);
            setPendingLoadingPicture(false)
        }).catch(err => (setPendingLoadingPicture(false)));
    }

    const XlsxParam =():StatsModels.ParamsXlsx =>{
        const fixDate = (date: Date | null,  startOfDay: boolean) => {
            // We have to send the date with a UTC timezone, or the server will get the value wrong
            var d1 = date ? date : new Date()
            if(startOfDay)var d = new Date(Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate(), 0, 0, 0, 0));
            else var d = new Date(Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate(), 23, 59, 59, 0));
            return d
        }
        const requestTypeParam = requestType === 0 ? "requests" : "invoicing"
        const areaList=()=>{
            return areas.filter((x) => selectedAreas.indexOf(x.id) > -1);
        }
        const statusList=()=>{
            return chosenStatusType.filter((x: any) => selectedStatus.indexOf(x.id) > -1)
        }
        return {
            location:0,
            body:{
                Start: fixDate(fromDate, true), 
                End: fixDate(toDate, false),
                Meters: [0],
                Columns1:exportColumnsParkant,
                LicenseType: licenseType, 
                Areas: areaList(),
                Status: statusList(),
                requestType: requestTypeParam,
            }
        }
    }
   
    const downloadCSV = async () => {
  //      CSV.exportDigitalLicensesCSV("events.csv", columnsParkant.map(column => {return "" + column.Header}), data.result)
//  uncomment this section when the backend is ready with excel download
        setPendingLoadingPicture(true) // start loading symbol
        await mobilbase.requestXlsx("Digiale_licenser.xlsx","digialelicens",XlsxParam()) // put new async function here and removed the old download function
        .then((res) => {
            setPendingLoadingPicture(false) // ends loading symbol
        })
        .catch((err) =>{
            setPendingLoadingPicture(false) // end loading symbol
            setShowError(true) // if something fails, start error message
        })    
        
    }

    return (
        <>
        <Grid item xs={12} style={{display: "flex", justifyContent: "center"}}>
        <Typography component="h1" variant="h5" style={{display: "flex", justifyContent: "center"}}> Digitale licenser </Typography>
        </Grid>
            <Container maxWidth={false}>
            <LoadingSymbols pendingLoadingPicture={pendingLoadingPicture} setPendingLoadingPicture={pendingLoadingPicture} open={pendingLoadingPicture} loadingSymbolText={"Henter data, vent venligst..."} />
            <RequestData />
            <div className={classes.paper}>
                <Container maxWidth={"lg"} className={classes.margTop}>
                    <MuiPickersUtilsProvider locale={daLocale} utils={DateFnsUtils}>
                        <Grid container spacing={2} alignItems="center" justify="center">
                            <Grid item xs={6}>
                            <FormControl variant="outlined" fullWidth margin="normal">
                                    <InputLabel ref={labelRequestRef} shrink htmlFor="request-type-input">Statistik</InputLabel>
                                    <Select label={"Statistik"} id="request-type-input" value={requestType} onChange={handleRequestTypeChange}>
                                        <MenuItem key={0} value={0}>Ansøgninger</MenuItem>
                                        <MenuItem key={1} value={1}>Faktureringsgrundlag</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <Divider></Divider>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl variant="outlined" fullWidth margin="normal">
                                    <InputLabel ref={labelTypeRef} shrink htmlFor="license-type-input">Licenstype</InputLabel>
                                    <Select label={"Licenstype"} id="license-type-input" value={licenseType} onChange={handleLicenseTypeChange}>
                                        <MenuItem key={-1} value={-1}>Alle</MenuItem>
                                        <MenuItem key={0} value={0}>Parkeringstilladelser</MenuItem>
                                        <MenuItem key={1} value={1}>Beboer- og erhvervslicens</MenuItem>
                                        <MenuItem key={2} value={2}>Håndværkerlicens</MenuItem>
                                        <MenuItem key={3} value={3}>Dispensationer</MenuItem>
                                        <MenuItem key={4} value={4}>Kørsels-/ parkeringstilladelse ved Landsstævne 2022</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl disabled={licenseType === -1} variant="outlined" fullWidth margin="normal">
                                    <InputLabel ref={labelAreaRef} htmlFor="area-input">{areaLabelText}</InputLabel>
                                    <Select label={areaLabelText} id="area-input" multiple value={selectedAreas} renderValue={(selected) => areas.filter((x) => selectedAreas.indexOf(x.id) > -1).map((x) => (x.area)).join(", ")} onChange={handleAreasChange}>
                                        { (areas.length > 0)
                                        ?( <MenuItem>
                                                <FormControlLabel 
                                                control={
                                                    <Checkbox checked={selectAreaBool} onChange={() => selectAllAreas()} />
                                                }
                                                label="Alle områder"
                                                />
                                            </MenuItem>
                                        ):(<></>)
                                        }
                                        {areas.map((a) => (
                                        <MenuItem key={a.id} value={a.id}>
                                                <Checkbox checked={selectedAreas.indexOf(a.id) > -1} />
                                                <ListItemText primary={a.area} />
                                        </MenuItem>))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <KeyboardDatePicker fullWidth disableToolbar variant="inline" inputVariant="outlined" format="dd-MM-yyyy" margin="normal" id="date-picker-inline" label="Fra dato" value={fromDate} onChange={setFromDate} KeyboardButtonProps={{'aria-label': 'Set dato'}} />
                            </Grid>
                            <Grid item xs={6}>
                                <KeyboardDatePicker fullWidth disableToolbar variant="inline" inputVariant="outlined" format="dd-MM-yyyy" margin="normal" id="date-picker-inline" label="Til dato" value={toDate} onChange={setToDate} KeyboardButtonProps={{'aria-label': 'Set dato'}} />
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl variant="outlined" fullWidth margin="normal">
                                    <InputLabel ref={labelStatusRef} htmlFor="area-input">{statusLabelText}</InputLabel>
                                    <Select label={areaLabelText} id="area-input" multiple value={selectedStatus} 
                                    renderValue={(selected) => chosenStatusType.filter((x: any) => selectedStatus.indexOf(x.id) > -1).map((x: any) => (x.status)).join(", ")} 
                                    onChange={handleStatusChange}>
                                        { (chosenStatusType.length > 0)
                                        ?( <MenuItem>
                                                <FormControlLabel 
                                                control={
                                                    <Checkbox checked={selectStatusBool} onChange={() => selectAllStatus()} />
                                                }
                                                label="Alle Status"
                                                />
                                            </MenuItem>
                                        ):(<></>)
                                        }
                                        {chosenStatusType.map((a: any) => (
                                        <MenuItem key={a.id} value={a.id}>
                                                <Checkbox checked={selectedStatus.indexOf(a.id) > -1} />
                                                <ListItemText primary={a.status} />
                                        </MenuItem>))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={8}>
                                <Button variant="contained" color="primary" className={classes.buts} onClick={() => setRun(true)}>Hent statistik</Button>
                            </Grid>
                            <Grid item xs={8}>
                                <Button disabled={data.result.length < 1} variant="contained" color="primary" className={classes.buts} onClick={downloadCSV}>Eksportér</Button>
                            </Grid>
                            <Grid item xs={8} sm={8}>
                                <Button variant='contained' color="primary" className={classes.buts} onClick={() => downloadPriceList()}> Hent prisliste </Button>
                            </Grid>
                        </Grid>
                    </MuiPickersUtilsProvider>
                    {/* </Paper> */}
                {/* </div> */}
                </Container>
                <div className={classes.grid}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <DigitalLicensTable<StatsModels.DigitalLicensesStatsData> columns={columnsSummary} data={summaryData} initialState={{pageSize: 150}} clickEvent={() => {}} update={null} pending={false} text={""} columnFilter={false} />
                        </Grid>
                    </Grid>
                </div>
                <div className={classes.grid}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <DigitalLicensTable<StatsModels.DigitalLicensesStatsData> columns={columnsParkant} data={data.result} initialState={{pageSize: 150}} clickEvent={() => {}} update={null} pending={false} text={""} columnFilter={true} />
                        </Grid>
                    </Grid>
                </div>
            </div>
            <NoDataDialog open={showError} onClose={() => { setShowError(false) }} title={"Intet svar"} text={"Noget gik galt, prøv venligst igen senere."}/>
        </Container>
        </>
    )
}

export default DigitalLicensesStatistics
